import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineChevronLeft from '~icons/ic/baseline-chevron-left'
import IcBaselineChevronRight from '~icons/ic/baseline-chevron-right'
import { useMemoizedNewsArticleData } from 'src/features/NewsArticle/util'
import type { Notification } from 'src/services/apollo/generated/graphql'
import { NotificationType } from 'src/services/apollo/generated/graphql'
import { useListNotifications } from 'src/services/apollo/notification'
import { getNodesFromConnection } from 'src/utils/graphql'
import { track } from 'src/utils/tracker'
import { CircularSpinner } from '../../GenerationTask/Common/CircularSpinner'
import MarkdownRenderer from '../../ui/MarkdownRenderer'
import { openNotificationNewsModal } from './NotificationNewsPreviewModal'

const DAY_MS = 24 * 3600 * 1e3

function groupNotisByDate(notis: Array<Notification>): NotiDateGroups {
  const groups: Record<string, Array<Notification>> = {}
  let lastDate = new Date()
  notis.forEach(noti => {
    const dateCreatedAt = new Date(noti.createdAt as string)
    const delta = Math.abs(lastDate.getTime() - dateCreatedAt.getTime())
    if (delta > DAY_MS) {
      lastDate = dateCreatedAt
    }
    groups[lastDate.toLocaleDateString()] = [
      ...(groups[lastDate.toLocaleDateString()] ?? []),
      noti,
    ]
  })
  return Object.entries(groups).map(([date, notis]) => ({ date, notis }))
}

type NotiDateGroups = Array<{ date: string; notis: Array<Notification> }>

export default function NotificationNewsContent() {
  const { data: notisData, loading: notisNewsLoading } = useListNotifications({
    last: 20,
    type: NotificationType.News,
  })
  const { t } = useTranslation(['common'])

  const notis = useMemo(
    () => getNodesFromConnection(notisData).reverse(),
    [notisData],
  )

  const notiGroups = useMemo(() => {
    return groupNotisByDate(notis ?? [])
  }, [notis])

  return (
    <>
      {notiGroups.length < 1 ? (
        notisNewsLoading ? (
          <CircularSpinner />
        ) : (
          <p className="w-full text-xl md:text-2xl text-slate-400 dark:text-slate-500 pb-4 text-center">
            {t('common:news.empty-placeholder')}
          </p>
        )
      ) : (
        <NewsDateTabs groups={notiGroups} />
      )}
    </>
  )
}

function NewsDateTabs(props: { groups: NotiDateGroups }) {
  const { t } = useTranslation(['common'])
  const { groups } = props
  const [currentGroupIdx, setCurrentGroupIdx] = useState<number>(0)

  const group = useMemo(() => {
    return groups[currentGroupIdx]
  }, [currentGroupIdx])

  return (
    <>
      <div className="w-full flex items-center px-6">
        <h3 className="font-bold text-xs">{group.date}</h3>
        <button
          disabled={currentGroupIdx === 0}
          className="ml-auto disabled:opacity-20 disabled:cursor-not-allowed"
          onClick={() => setCurrentGroupIdx(g => g - 1)}
        >
          <IcBaselineChevronLeft />
        </button>
        <button
          disabled={currentGroupIdx >= groups.length - 1}
          className="disabled:opacity-20 disabled:cursor-not-allowed"
          onClick={() => setCurrentGroupIdx(g => g + 1)}
        >
          <IcBaselineChevronRight />
        </button>
      </div>
      <div className="h-px mx-6 mt-3 bg-slate-300"></div>
      <NewsList items={group.notis} />
      <div className="py-4 flex justify-center">
        <a
          href="/news"
          target="_blank"
          className="inline-flex items-center gap-1 text-sm text-theme-primary"
          onClick={() => track('see_all_news_click')}
        >
          {t('common:news.buttons.see-all-news.label')}
          <IcBaselineChevronRight className="size-5" />
        </a>
      </div>
    </>
  )
}

function NewsList(props: { items: Notification[] }) {
  return (
    <div className="max-h-[min(400px,calc(100vh-160px))] md:max-h-[min(800px,calc(100vh-160px))] overflow-y-auto flex flex-col gap-4 bg-[#9333EA]/[0.03] px-6 py-2">
      {props.items.map(item => (
        <NewsItem key={item.id} item={item} />
      ))}
    </div>
  )
}

function NewsItem(props: { item: Notification }) {
  const { item } = props

  const curLocaleItem = useMemoizedNewsArticleData(item)

  return (
    <div
      className="pl-4 relative cursor-pointer hover:dark:bg-gray-700 hover:bg-gray-100 transition rounded-sm max-w-full"
      role="button"
      onClick={() => {
        track('news_content_click')
        openNotificationNewsModal(curLocaleItem)
      }}
    >
      <div className="absolute h-[calc(100%-10px)] w-px top-2 left-0 bg-theme-primary" />
      <div className="rounded-full w-2 h-2 bg-theme-primary absolute top-2 left-0 -translate-x-1/2" />
      <h3 className="font-bold mb-2">{curLocaleItem.title}</h3>
      <MarkdownRenderer
        as="p"
        inline
        className="text-neutral-500 dark:text-neutral-400 line-clamp-2"
      >
        {curLocaleItem.content?.slice(0, 200) ?? ''}
      </MarkdownRenderer>
    </div>
  )
}
